import React, { useState, useEffect } from 'react'
import './Windchill.css'
import Config from '../utils/config'
import { AuthProvider } from '../auth/authlib'

type Alternate = {
  bmName: string
  bmId: string
  bmDensity: string
  stName: string
  stId: string
  stDensity: string
  coverage: string
  thickness: string
  conditionType: string
  conditionCode: string
}

type Part = {
  id: string
  partNumber: string
  name: string
  bmName: string
  bmId: string
  bmDensity: string
  stName: string
  stId: string
  stDensity: string
  coverage: string
  thickness: string
  conditionType: string
  conditionCode: string
  alternates: Alternate[]
}

type ContextMenu = { x: number; y: number; rowId: string } | null

function Windchill(): JSX.Element {
  const [data, setData] = useState<Part[]>([
    { id: '1', partNumber: '100-001', name: 'Gear Assembly', bmName: 'Steel',    bmId: '9000229',    bmDensity: '7.85', stName: 'ST-1001', stId: '30', stDensity: '1.2', coverage: '0.9', thickness: '0.05', conditionType: '', conditionCode: '',
      alternates: [
        { bmName: 'Steel Grade 2',   bmId: '9000415', bmDensity: '7.85', stName: 'ST-1001', stId: '30', stDensity: '1.2', coverage: '0.9', thickness: '0.05', conditionType: '', conditionCode: '' },
        { bmName: 'Stainless Steel', bmId: '9000872', bmDensity: '8.00', stName: '',        stId: '',   stDensity: '',    coverage: '',    thickness: '',      conditionType: '', conditionCode: '' },
      ],
    },
    { id: '2', partNumber: '100-002', name: 'Shaft',         bmName: 'Aluminum', bmId: '12',         bmDensity: '2.7',  stName: '',       stId: '31', stDensity: '',    coverage: '',    thickness: '',      conditionType: '', conditionCode: '', alternates: [] },
    { id: '3', partNumber: '100-003', name: 'Housing',       bmName: '',         bmId: '',           bmDensity: '',     stName: '',       stId: '',   stDensity: '',    coverage: '',    thickness: '',      conditionType: '', conditionCode: '', alternates: [] },
    { id: '4', partNumber: '100-004', name: 'Bearing',       bmName: 'Bronze',   bmId: '1SAA100001', bmDensity: '22.8', stName: 'lack',   stId: '1SAA100254', stDensity: '111', coverage: '0.8', thickness: '0.03', conditionType: '', conditionCode: '', alternates: [] },
    { id: '5', partNumber: '100-005', name: 'Coupling',      bmName: '',         bmId: '',           bmDensity: '',     stName: '',       stId: '',   stDensity: '',    coverage: '',    thickness: '',      conditionType: '', conditionCode: '', alternates: [] },
    { id: '6', partNumber: '100-006', name: 'Washer',        bmName: '',         bmId: '',           bmDensity: '',     stName: '',       stId: '',   stDensity: '',    coverage: '',    thickness: '',      conditionType: '', conditionCode: '', alternates: [] },
  ])
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['1']))
  const [contextMenu, setContextMenu] = useState<ContextMenu>(null)
  const [prostepWindow, setProstepWindow] = useState<Window | null>(null)
  const authProvider = new AuthProvider()

  useEffect(() => {
    const handleClick = () => setContextMenu(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && Config.propertyContains('fromWindchillOrigins', event?.origin)) {
        if (event.data === 'ready') {
          const selectedItems = data
            .filter((row) => selectedRows.has(row.id))
            .map((row) => ({
              partNumber: row.partNumber,
              bmId: row.bmId,
              stMaterialId: row.stId,
              bmName: row.bmName,
              stName: row.stName,
              isAssembly: String(row.name).toLowerCase().includes('assembly') || false,
              bmDensity: row.bmDensity,
              stDensity: row.stDensity,
              coverage: row.coverage,
              thickness: row.thickness,
              alternates: row.alternates.map(a => ({
                partNr: row.partNumber,
                bmId: a.bmId || null,
                bmName: a.bmName || null,
                bmDensity: a.bmDensity || null,
                conditionType: a.conditionType || null,
                conditionCode: a.conditionCode || null,
                condition: a.conditionCode ? { code: a.conditionCode } : null,
              })),
            }))
          if (prostepWindow && !prostepWindow.closed) {
            prostepWindow.postMessage(selectedItems, window.location.origin)
          } else {
            console.warn('Prostep window was closed before sending selection')
          }
          return
        }
        // event.data is the old single-object assignment format from Prostep
        const assignment = (event.data as any) || {}
        console.log('Received assignment from Prostep:', assignment)

        const valOf = (v: any): string => v == null ? '' : (typeof v === 'object' ? String(v.value ?? '') : String(v))
        const newExpandedRows = new Set(expandedRows)
        const newData = data.map((r) => {
          if (!selectedRows.has(r.id)) {
            return r
          }
          const inboundAlternates: Alternate[] = Array.isArray(assignment.alternates)
            ? assignment.alternates
                .filter((a: any) => !a.partNr || a.partNr === r.partNumber)
                .map((a: any) => ({
                  bmName: a.bmName ?? '',
                  bmId:   a.bmId   ?? '',
                  bmDensity: valOf(a.bmDensity),
                  stName: '', stId: '', stDensity: '',
                  coverage: '', thickness: '',
                  conditionType: a.conditionType ?? '', conditionCode: a.conditionCode ?? '',
                }))
            : r.alternates
          if (inboundAlternates.length > 0) {
            newExpandedRows.add(r.id)
          } else {
            newExpandedRows.delete(r.id)
          }
          return {
            ...r,
            bmName: assignment?.bmName ?? r.bmName,
            bmId: assignment?.bmId ?? r.bmId,
            bmDensity: assignment?.bmDensity != null ? valOf(assignment.bmDensity) : r.bmDensity,
            stName: assignment?.stName ?? r.stName,
            stId: assignment?.stMaterialId ?? '',
            stDensity: assignment?.stDensity != null ? valOf(assignment.stDensity) : '',
            coverage: assignment?.stCoverage != null ? valOf(assignment.stCoverage) : r.coverage,
            thickness: assignment?.stThickness != null ? valOf(assignment.stThickness) : '',
            conditionType: assignment?.conditionType ?? r.conditionType,
            conditionCode: assignment?.condition?.code ?? r.conditionCode,
            alternates: inboundAlternates,
          }
        })
        setData(newData)
        setExpandedRows(newExpandedRows)
        setSelectedRows(new Set())
        if (prostepWindow && !prostepWindow.closed) {
          //prostepWindow.close()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [selectedRows, prostepWindow, data])

  const handleCheckboxChange = (id: string) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedRows(newSelected)
  }

  const handleToggleExpand = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(new Set(data.map((row) => row.id)))
    } else {
      setSelectedRows(new Set())
    }
  }

  const handleContextMenu = (e: React.MouseEvent, rowId: string) => {
    e.preventDefault()
    if (!selectedRows.has(rowId)) {
      return
    }
    setContextMenu({ x: (e as any).pageX, y: (e as any).pageY, rowId })
  }

  const handleAssignMaterial = () => {
    if (selectedRows.size === 0) {
      alert('Please select at least one row')
      return
    }
    let path = '/prostep?'
    if (authProvider.isAuthSkipped()) {
      path += 'skipAuth=true&'
    }
    if (Config.get('additionalWizzardParams')) {
      path += Config.get('additionalWizzardParams')
    }
    const newWindow = window.open(path, 'Prostep', 'width=1200,height=800,scrollbars=no,resizable=yes')
    setProstepWindow(newWindow)
    setContextMenu(null)
  }

  const allSelected = data.length > 0 && selectedRows.size === data.length

  return (
    <div className="windchill-container">
      <div className="windchill-toolbar">
        <button className="windchill-toolbar-btn">File</button>
        <button className="windchill-toolbar-btn">Edit</button>
        <button className="windchill-toolbar-btn">View</button>
        <button className="windchill-toolbar-btn">Tools</button>
        <div className="windchill-toolbar-separator"></div>
        <button className="windchill-toolbar-btn" onClick={() => handleAssignMaterial()}>
          Actions ▼
        </button>
      </div>

      <div className="windchill-header">
        <h2>Parts List - Product Structure</h2>
        <button className="assign-material-btn" onClick={() => handleAssignMaterial()}>
          Assign Material
        </button>
      </div>

      <div className="windchill-nav-breadcrumb">
        <span>Home</span>
        <span className="separator">›</span>
        <span>Product</span>
        <span className="separator">›</span>
        <span>Structure Browser</span>
      </div>

      <div className="table-container">
        <table className="windchill-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
              </th>
              <th>Number</th>
              <th>Name</th>
              <th>Material Spec</th>
              <th>BM ID</th>
              <th>BM Density</th>
              <th>ST Name</th>
              <th>ST ID</th>
              <th>ST Density</th>
              <th>Coverage</th>
              <th>Thickness</th>
              <th>Condition Type</th>
              <th>Condition Code</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {data.flatMap((row) => {
              const isExpanded = expandedRows.has(row.id)
              const hasAlternates = row.alternates.length > 0
              const baseRow = (
                <tr
                  key={row.id}
                  className={selectedRows.has(row.id) ? 'selected' : ''}
                  onContextMenu={(e) => handleContextMenu(e, row.id)}
                >
                  <td className="checkbox-col">
                    <input type="checkbox" checked={selectedRows.has(row.id)} onChange={() => handleCheckboxChange(row.id)} />
                  </td>
                  <td className="number-col">
                    {hasAlternates && (
                      <button className="expand-toggle" onClick={(e) => { e.stopPropagation(); handleToggleExpand(row.id) }}>
                        {isExpanded ? '▾' : '▸'}
                      </button>
                    )}
                    {row.partNumber}
                  </td>
                  <td className="name-col">{row.name}</td>
                  <td>{row.bmName || ''}</td>
                  <td>{row.bmId || ''}</td>
                  <td>{row.bmDensity || ''}</td>
                  <td>{row.stName || ''}</td>
                  <td>{row.stId || ''}</td>
                  <td>{row.stDensity || ''}</td>
                  <td>{row.coverage || ''}</td>
                  <td>{row.thickness || ''}</td>
                  <td>{row.conditionType || ''}</td>
                  <td>{row.conditionCode || ''}</td>
                  <td>{row.bmId ? <span className="type-badge type-base">Base</span> : ''}</td>
                </tr>
              )
              const alternateRows = isExpanded
                ? row.alternates.map((alt, i) => (
                    <tr key={`${row.id}-alt-${i}`} className="alternate-row">
                      <td className="checkbox-col"></td>
                      <td className="alternate-indent"></td>
                      <td></td>
                      <td>{alt.bmName}</td>
                      <td>{alt.bmId}</td>
                      <td>{alt.bmDensity}</td>
                      <td>{alt.stName}</td>
                      <td>{alt.stId}</td>
                      <td>{alt.stDensity}</td>
                      <td>{alt.coverage}</td>
                      <td>{alt.thickness}</td>
                      <td>{alt.conditionType}</td>
                      <td>{alt.conditionCode}</td>
                      <td><span className="type-badge type-alternate">Alternate</span></td>
                    </tr>
                  ))
                : []
              return [baseRow, ...alternateRows]
            })}
          </tbody>
        </table>
      </div>

      <div className="windchill-statusbar">
        <span>
          {data.length} object(s) | {selectedRows.size} selected
        </span>
        <span>Windchill 13.1</span>
      </div>

      {contextMenu && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <div className="context-menu-item" onClick={() => handleAssignMaterial()}>Assign Material</div>
          <div className="context-menu-separator"></div>
          <div className="context-menu-item" style={{ opacity: 0.5 }}>View Details</div>
          <div className="context-menu-item" style={{ opacity: 0.5 }}>Check Out</div>
        </div>
      )}
    </div>
  )
}

export default Windchill
